import { computed, readonly } from "vue";
import { useStore } from "vuex";

import cloneDeep from "lodash/cloneDeep";
import findLast from "lodash/findLast";
import last from "lodash/last";
import { storeToRefs } from "pinia";

import {
  UNDERWRITING_ATTRIBUTES_AND_SERVICES_BY_SECTION,
  UNDERWRITING_STATUS_FAIL,
} from "@/helpers/constants/underwriting";
import { useDeals } from "@/hooks/deals";
import { useUnderwritingLogsQuery as useUnderwritingQuery } from "@/hooks/queries/useUnderwriting";
import useApplicationsStore from "@/stores/applications";

import type { IDataOrchestrationHistoryStepContext } from "@/models/applications";
import type {
  IUnderwritingLog,
  IUnderwritingLogEntry,
  UnderwritingLogInitiatorType,
  UnderwritingStatusType,
} from "@/models/underwriting";

interface ServiceOutcome {
  outcome: UnderwritingStatusType;
  service: string;
  initiator: string;
  date: string;
}

export const useUnderwritingLogs = () => {
  const { getters } = useStore();
  const { activeDeal } = useDeals();
  const applicationId = computed(() => activeDeal.value?.id || "");

  const { data: queryData, isLoading: isQueryLoading } =
    useUnderwritingQuery(applicationId);

  const applicationsStore = useApplicationsStore();
  const { activeDataOrchestrationLog, gettingOrchestrationLogs } =
    storeToRefs(applicationsStore);

  const isLoadingUnderwritingLogs = computed<boolean>(() => {
    return isQueryLoading.value;
  });

  const underwritingLogs = computed<IUnderwritingLog[]>(() => {
    return queryData.value || [];
  });

  const internalError = computed(
    () => activeDataOrchestrationLog.value?.internal_error
  );

  const outcome = computed(
    () => activeDataOrchestrationLog.value?.outcome || ""
  );

  const historyLogs = computed(
    () => activeDataOrchestrationLog.value?.history || []
  );

  const lastHistoryLog = computed(() => last(historyLogs.value));

  const automatedHistoryLogs = computed(
    () => historyLogs.value?.filter(({ type }) => type === 1) || []
  );

  const newestAutomatedHistoryLog = computed(() => {
    return last(automatedHistoryLogs.value);
  });

  const masterStatus = computed(
    () => activeDataOrchestrationLog.value?.master_status
  );

  const lastDataOrchestrationHistoryLog = computed(() => {
    if (!lastHistoryLog.value?.context?.manual_update) {
      return lastHistoryLog.value;
    }
    return findLast(historyLogs.value, (log) => !log.context?.manual_update);
  });

  const isOrchestrationPaused = computed(
    () =>
      newestAutomatedHistoryLog.value?.paused_at &&
      !newestAutomatedHistoryLog.value?.resumed_at
  );

  const attributesAndServicesToSectionMap = computed(() => {
    return Object.entries(
      UNDERWRITING_ATTRIBUTES_AND_SERVICES_BY_SECTION
    ).reduce(
      (
        attributesAndServicesAccumulator: Record<string, string>,
        [section, attributesAndServicesList]
      ) => {
        const reversed: Record<string, string> = [
          ...attributesAndServicesList,
        ].reduce(
          (temporaryAccumulator, attributeOrService) => ({
            ...temporaryAccumulator,
            [attributeOrService]: section,
          }),
          {}
        );
        return { ...attributesAndServicesAccumulator, ...reversed };
      },
      {}
    );
  });

  const sectionOutcomes = computed(() => {
    const result: Record<string, UnderwritingStatusType> = {};
    const logs = historyLogs.value?.reduce(
      (logAcc: IDataOrchestrationHistoryStepContext[], historyLog) => [
        ...logAcc,
        ...(historyLog?.context?.log || []),
      ],
      []
    );
    logs?.forEach((logLine) => {
      const service =
        attributesAndServicesToSectionMap.value[logLine.attribute];
      if (logLine.outcome_result) {
        result[service] = logLine.outcome;
        return;
      }
      result[service] = UNDERWRITING_STATUS_FAIL;
    });
    if (activeDataOrchestrationLog.value?.internal_error) {
      const step =
        activeDataOrchestrationLog.value.template?.content?.steps?.find(
          (step) =>
            step.id === (newestAutomatedHistoryLog.value?.step_id as string)
        );
      step?.attributes?.forEach(
        (attribute) =>
          (result[attributesAndServicesToSectionMap.value[attribute.id]] =
            UNDERWRITING_STATUS_FAIL)
      );
    }
    return result;
  });

  const hasUnderwritingLogs = computed(
    () => (underwritingLogs.value?.length || 0) > 0
  );

  const initiatorTypeBySection = computed<
    Record<string, UnderwritingLogInitiatorType>
  >(() => {
    if (!hasUnderwritingLogs.value) {
      return {};
    }
    const logs = cloneDeep(underwritingLogs.value);
    // We are reversing the order of both here to ensure that the latest entries and logs are applied last
    return logs?.reverse().reduce((logInitiatorAccumulator, log) => {
      const entries = log.entries
        ?.reverse()
        .reduce((entriesInitiatorAccumulator, entry: IUnderwritingLogEntry) => {
          const section =
            attributesAndServicesToSectionMap.value[entry.service_name];
          return {
            ...entriesInitiatorAccumulator,
            [section]: log.initiator_type,
          };
        }, {});
      return { ...logInitiatorAccumulator, ...entries };
    }, {});
  });

  const outcomes = computed<Record<string, string>>(
    () => getters["options/outcomes"]
  );

  const underwritingLogsWithServices = computed(() => {
    if (!hasUnderwritingLogs.value) {
      return [];
    }

    const statusToOutcome = Object.keys(outcomes.value).reduce(
      (res, value, index) => {
        res[index + 1] = value as UnderwritingStatusType;
        return res;
      },
      {} as Record<string, UnderwritingStatusType>
    );

    const logs = cloneDeep(underwritingLogs.value);
    return logs.reverse().reduce((logInitiatorAccumulator, log) => {
      const mappedEntries = log.entries.reverse().map((entry) => ({
        service: entry.service_name,
        initiator: log.initiator_type || "user",
        outcome:
          statusToOutcome[String(entry.status) as keyof typeof statusToOutcome],
        date: log.finished_at || log.started_at || "",
      }));
      logInitiatorAccumulator.push(...mappedEntries);
      return logInitiatorAccumulator;
    }, [] as ServiceOutcome[]);
  });

  return {
    lastHistoryLog,
    lastDataOrchestrationHistoryLog,
    sectionOutcomes,
    internalError,
    outcome,
    masterStatus,
    newestAutomatedHistoryLog,
    isOrchestrationPaused,
    underwritingLogs,
    underwritingLogsWithServices,
    hasUnderwritingLogs,
    isLoadingUnderwritingLogs,
    initiatorTypeBySection,
    automatedHistoryLogs,
    historyLogs,
    gettingHistoryLogs: readonly(gettingOrchestrationLogs),
  };
};
